import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft } from "lucide-react";
import { NestIcon } from "../components/NestIcon";

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <NestIcon className="h-8 w-8 text-primary" />
            <span className="text-xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>therentpayer</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
          Terms of Service
        </h1>
        <p className="text-muted-foreground mb-8">Last updated: February 22, 2026</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Agreement to Terms</h2>
            <p className="text-muted-foreground mb-4">
              By accessing or using TheRentPayer's rent insurance platform ("Service"), you agree to be bound 
              by these Terms of Service. If you disagree with any part of these terms, you may not 
              access the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground mb-4">
              TheRentPayer provides rent protection insurance that covers up to three months of rent payments 
              in the event of involuntary job loss. Our Service includes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Quote generation and policy purchase</li>
              <li>Claims filing and processing</li>
              <li>Account management and document storage</li>
              <li>Customer support and assistance</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. Eligibility</h2>
            <p className="text-muted-foreground mb-4">
              To use our Service, you must:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Be at least 18 years of age</li>
              <li>Be a legal resident of the United States</li>
              <li>Have a valid rental agreement</li>
              <li>Be currently employed or have a verifiable income source</li>
              <li>Provide accurate and truthful information</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. User Accounts</h2>
            <p className="text-muted-foreground mb-4">
              When you create an account, you agree to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your password</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Premium Payments</h2>
            <p className="text-muted-foreground mb-4">
              By purchasing a policy, you agree to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Pay the quoted premium amount plus applicable fees</li>
              <li>Maintain active payment method for recurring charges</li>
              <li>Pay premiums by the due date to maintain coverage</li>
              <li>Understand that missed payments may result in policy cancellation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Coverage Terms</h2>
            <h3 className="text-xl font-medium mb-2">Waiting Period</h3>
            <p className="text-muted-foreground mb-4">
              Coverage for claims begins after a 6-month waiting period from your policy start date. 
              Claims filed before this period will not be honored.
            </p>
            
            <h3 className="text-xl font-medium mb-2">Covered Events</h3>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Involuntary layoff or termination</li>
              <li>Company closure or bankruptcy</li>
              <li>Position elimination or reduction in force</li>
            </ul>
            
            <h3 className="text-xl font-medium mb-2">Exclusions</h3>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Voluntary resignation</li>
              <li>Termination for cause (misconduct, poor performance)</li>
              <li>Self-employment or contract work ending</li>
              <li>Retirement or seasonal employment ending</li>
              <li>Pre-existing knowledge of job loss at time of enrollment</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Claims Process</h2>
            <p className="text-muted-foreground mb-4">
              To file a claim, you must:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Have an active policy for at least 12 months</li>
              <li>Submit required documentation (termination letter, ID, pay stubs)</li>
              <li>File for unemployment benefits with your state</li>
              <li>Cooperate with our verification process</li>
              <li>File within 30 days of job loss</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Loyalty Rewards Program</h2>
            <p className="text-muted-foreground mb-4">
              Members who maintain their policy for 12 consecutive months without filing a claim 
              are eligible for a 15% cashback reward. Terms apply:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>All 12 monthly payments must be made on time</li>
              <li>No claims filed during the 12-month period</li>
              <li>Reward is credited to your TheRentPayer wallet</li>
              <li>Reward calculation based on premiums paid</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Cancellation and Refunds</h2>
            <p className="text-muted-foreground mb-4">
              You may cancel your policy at any time. Refunds are provided as follows:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Cancellation within 30 days: Full refund of premiums paid</li>
              <li>Cancellation after 30 days: No refund of premiums</li>
              <li>Signup fee is non-refundable after 30 days</li>
              <li>Early cancellation forfeits loyalty rewards eligibility</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Prohibited Activities</h2>
            <p className="text-muted-foreground mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Provide false or misleading information</li>
              <li>File fraudulent claims</li>
              <li>Share your account credentials</li>
              <li>Attempt to manipulate or abuse the system</li>
              <li>Use the Service for unlawful purposes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. Limitation of Liability</h2>
            <p className="text-muted-foreground mb-4">
              To the maximum extent permitted by law, TheRentPayer shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages resulting from your use of 
              or inability to use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Dispute Resolution</h2>
            <p className="text-muted-foreground mb-4">
              Any disputes arising from these Terms shall be resolved through binding arbitration 
              in accordance with the rules of the American Arbitration Association. Class action 
              lawsuits and class-wide arbitration are not permitted.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">13. Changes to Terms</h2>
            <p className="text-muted-foreground mb-4">
              We reserve the right to modify these Terms at any time. We will notify you of 
              material changes via email or through the Service. Continued use after changes 
              constitutes acceptance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">14. Governing Law</h2>
            <p className="text-muted-foreground mb-4">
              These Terms are governed by and construed in accordance with the laws of the 
              State of Delaware, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">15. Contact Information</h2>
            <p className="text-muted-foreground mb-4">
              For questions about these Terms, please contact us:
            </p>
            <ul className="list-none text-muted-foreground">
              <li>Email: legal@therentpayer.com</li>
              <li>Address: 123 Insurance Lane, Suite 100, New York, NY 10001</li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} TheRentPayer. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
